import moment from 'moment';
import { SymptoData } from '../types';
import { extractDemographics } from './epicUtils';
import { generateNavReasonsForMicrostrategy } from './reasonsForNav';
import { DEFAULT_PROVIDER, SERVICE_INTRO_ENCOUNTER_TEXT, INVALID_ED_DISPOSITION, getLanguage } from './genericUtils';

export const formatCSVData = async (data: Record<string, string>): Promise<{
  type: 'error', error: string,
} | {
  type: 'ok', data: Omit<SymptoData, 'Intro Message' | 'Full Site Name' | 'Payor Category' | 'Secondary Navigator Name'>, warnings: Array<string>,
}> => {
  const warnings = [];
  const demographics = await extractDemographics(data);
  if (demographics.type === 'error') {
    return { type: 'error', error: demographics.error };
  }
  const { data: demographicsData, warning: demographicsWarning } = demographics;
  warnings.push(...(demographicsWarning || []));

  if (data['ED Disposition'] == null || data['ED Disposition'].trim().length === 0) {
    return { type: 'error', error: 'ED Disposition is empty' };
  }

  const VALID_DISPOSITIONS = [
    'Discharge',
    'Eloped',
    'Ama',
    'Lwbs After Triage',
    'Lwbs Before Triage',
  ]
  if (!VALID_DISPOSITIONS.includes(data['ED Disposition'].trim())) {
    return { type: 'error', error: `${INVALID_ED_DISPOSITION}: ${data['ED Disposition']}` };
  }

  const parsedEDReferralDate = moment(data['Arr Date'], 'M/DD/YYYY', true)
  if (!parsedEDReferralDate.isValid()) {
    warnings.push(`Invalid ED Arrival Date: ${data['Arr Date']}`);
  }

  if (demographicsData['Phone Number'] == null || demographicsData['Phone Number'].trim().length === 0) {
    return { type: 'error', error: warnings.join(',') };
  }
  const numEdVisits = String(data['ED Vis Last 90 Days'].trim().length > 0 ? Number(data['ED Vis Last 90 Days']) : 0);

  return {
    type: 'ok',
    data: {
      'ED Visit/Referral Date': moment(data['Arr Date'], 'MM/DD/YYYY').format('MM/DD/YYYY'),
      'Provider / On-Site Coordinator Name': DEFAULT_PROVIDER,
      'Patient\'s Primary Language': getLanguage(data['Language'], demographicsData.MRN),
      'Spanish Speaking': getLanguage((data['Language'] || '').trim(), demographicsData.MRN) === 'Spanish' ? 'TRUE' : 'FALSE',
      'Priority Level': '',
      'Reason for Navigation': generateNavReasonsForMicrostrategy({
        insuranceType: demographicsData['Payer Type'],
        numEDVisits6Months: Number(numEdVisits),
        site: demographicsData.Sites,
        chiefComplaint: demographicsData['Chief Complaint'],
        dischargeDiagnoses: demographicsData['Discharge Diagnosis'],
      }).join(','),
      'Encounter Details/Comments': SERVICE_INTRO_ENCOUNTER_TEXT,
      'Navigator Name': '',
      'Visits within 6 mo\'s': numEdVisits,
      // dont send service intro
      Identification: 'Service Intro No Response',
      'No Service Introduction': 'FALSE',
      ...demographicsData,
    },
    warnings,
  }
};
